'use client';

/**
 * One-shot `bounced` beacon emitter that fires on `pagehide` if the caller
 * decides the user is leaving without reaching a counted outcome. Lives
 * separately from the kind-specific funnel so both the Turnstile-gated and
 * ungated banking flows can mount it with their own resolution semantics.
 *
 * `getBeaconParams` is invoked at the moment `pagehide` fires. Returning
 * `null` suppresses the beacon (already navigated, already attributed a
 * specific failure, etc.). Returning a query-param object emits exactly
 * one `bounced` beacon with those tags.
 *
 * Why pagehide (and not beforeunload / visibilitychange):
 * - beforeunload is unreliable on mobile and blocks the unload path.
 * - visibilitychange:hidden fires on tab-switch too — counting those as
 *   bounces would over-attribute (the user may come back and complete).
 * - pagehide is the standard "page is being torn down" signal; iOS Safari
 *   may skip it on low-memory tab kills, so this is a lower-bound on real
 *   bounces (intentional — false negatives beat false positives here).
 *
 * Why navigator.sendBeacon for delivery: pagehide fires while the page is
 * being torn down, and any in-flight image / fetch request gets cancelled
 * along with it. sendBeacon is the only API the browser guarantees to
 * deliver after unload (queued by the user agent). We keep an Image()
 * fallback for environments without sendBeacon or when the queue is full.
 */
import { useEffect, useRef } from 'react';

interface Props {
  getBeaconParams: () => Record<string, string> | null;
}

export default function BounceTracker({ getBeaconParams }: Props): null {
  // Latest predicate ref so the pagehide listener always sees the current
  // resolution state instead of a value captured at mount.
  const getParamsRef = useRef(getBeaconParams);
  getParamsRef.current = getBeaconParams;

  useEffect(() => {
    let fired = false;
    const onPageHide = () => {
      if (fired) return;
      const params = getParamsRef.current();
      if (!params) return;
      fired = true;
      const search = new URLSearchParams({
        kind: 'bounced',
        ...params,
        t: String(Date.now()),
      });
      const url = `/redirect/beacon?${search.toString()}`;

      // navigator.sendBeacon is the API designed for unload-time delivery:
      // the browser queues the request and guarantees it gets sent even
      // after the page is torn down. new Image() requests get cancelled
      // when the page unloads, so a meaningful fraction of bounces never
      // reached the server with that approach. sendBeacon issues a POST
      // (with an empty body here) — the route accepts POST as an alias
      // for GET so all params travel in the query string.
      const sent =
        typeof navigator !== 'undefined' &&
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(url);
      if (!sent) {
        // Last-ditch fallback for environments without sendBeacon or where
        // the queue is full / the call returned false. Won't reliably
        // survive unload, but better than dropping the beacon entirely.
        new Image().src = url;
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  return null;
}
